﻿import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useRef } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useClientTheme } from '../../../../helpers/hooks/useClientTheme';
import { DateGroupType } from '../../../../model/active/dateGroupType';
import { IProcessedActivityTrackerInterval } from '../../../../model/passive/activityTrackerData';

// Number of days threshold: ranges with MORE than this many days use a bar chart.
// A range of exactly this many days or fewer uses a line chart.
// Change this value to adjust the breakpoint (e.g. set to 2 for "more than 2 days = bar").
const BAR_CHART_THRESHOLD_DAYS = 1;

const ChartContainer = styled(Box)({
  height: '100%',
  width: '100%',
  minHeight: '200px',
  position: 'relative',
});

const NoDataContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
});

interface CombinedActivityChartProps {
  data: IProcessedActivityTrackerInterval[];
  height?: number | string;
  selectedDateRange?: [Date | null, Date | null];
  dateType?: DateGroupType;
  chartRef?: React.MutableRefObject<any>;
  onZoomChange?: (chart: any) => void;
}

function getPeriodStart(date: Date, dateType: DateGroupType | undefined): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  if (dateType === DateGroupType.Week) {
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); // Monday
  } else if (dateType === DateGroupType.Month) {
    d.setDate(1);
  } else if (dateType === DateGroupType.Year) {
    d.setMonth(0, 1);
  }
  return d;
}

function aggregateByPeriod(
  items: IProcessedActivityTrackerInterval[],
  dateType: DateGroupType | undefined
): IProcessedActivityTrackerInterval[] {
  const map = new Map<number, IProcessedActivityTrackerInterval>();

  for (const item of items) {
    const periodStart = getPeriodStart(new Date(item.date), dateType);
    const key = periodStart.getTime();

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        date: periodStart.toISOString(),
        walk: 0,
        sit: 0,
        stand: 0,
        other: 0,
        notInUse: 0,
        totalUsage: 0,
        walkPercentage: 0,
        sitPercentage: 0,
        standPercentage: 0,
        otherPercentage: 0,
      });
    }

    const agg = map.get(key)!;
    agg.walk += item.walk;
    agg.sit += item.sit;
    agg.stand += item.stand;
    agg.other += item.other;
    agg.notInUse += item.notInUse;
    agg.totalUsage += item.totalUsage;
  }

  return Array.from(map.values()).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
}

const CombinedActivityChart: React.FC<CombinedActivityChartProps> = ({
  data,
  height = '100%',
  selectedDateRange,
  dateType,
  chartRef: externalChartRef,
  onZoomChange,
}) => {
  const { theme } = useClientTheme();
  const internalChartRef = useRef<any>(null);
  const chartRef = externalChartRef || internalChartRef;

  const calculateRangeDays = (): number => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return 1;
    const diffMs = selectedDateRange[1].getTime() - selectedDateRange[0].getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const rangeDays = calculateRangeDays();
  const useBarChart = true; // rangeDays > BAR_CHART_THRESHOLD_DAYS; --- IGNORE --- Always use bar chart for now per UX feedback, but keep the logic in place for easy reversion if needed.

  const getTimeConfig = () => {
    if (!useBarChart) {
      return {
        unit: 'hour' as const,
        displayFormats: { hour: 'HH:mm', day: 'dd MMM' },
        tooltipFormat: 'dd MMM yyyy HH:mm',
      };
    }
    // For bar charts, match the axis unit to the API dateType so bars are properly sized
    switch (dateType) {
      case DateGroupType.Week:
        return {
          unit: 'week' as const,
          displayFormats: { week: 'dd MMM' },
          tooltipFormat: 'dd MMM yyyy',
        };
      case DateGroupType.Month:
        return {
          unit: 'month' as const,
          displayFormats: { month: 'MMM yyyy' },
          tooltipFormat: 'MMM yyyy',
        };
      case DateGroupType.Year:
        return {
          unit: 'year' as const,
          displayFormats: { year: 'yyyy' },
          tooltipFormat: 'yyyy',
        };
      default:
        return {
          unit: 'day' as const,
          displayFormats: { day: 'dd MMM' },
          tooltipFormat: 'dd MMM yyyy',
        };
    }
  };

  const timeConfig = getTimeConfig();

  const getAxisBounds = () => {
    if (!selectedDateRange?.[0] || !selectedDateRange?.[1]) return {};
    const minDate = new Date(selectedDateRange[0]);
    minDate.setHours(0, 0, 0, 0);
    // Bar charts: only set min â€” setting max to end-of-day (â‰ˆ next day 00:00) causes Chart.js
    // to add an extra tick for the following day, leaving a blank bar slot on the right.
    if (useBarChart) {
      return { min: minDate.getTime() };
    }
    const maxDate = new Date(selectedDateRange[1]);
    maxDate.setHours(23, 59, 59, 999);
    return { min: minDate.getTime(), max: maxDate.getTime() };
  };

  const axisBounds = getAxisBounds();

  const toPoint = (date: string, y: number) => ({
    x: new Date(date).getTime(),
    y,
  });

  const activeTotal = (item: IProcessedActivityTrackerInterval): number =>
    item.walk + item.sit + item.stand + item.other;

  const toPct = (value: number, total: number): number =>
    total === 0 ? 0 : (value / total) * 100;

  const formatBarChartData = (): any => {
    const aggregated = aggregateByPeriod(data, dateType);
    return {
      datasets: [
        {
          label: 'Walk',
          data: aggregated.map((item) => toPoint(item.date, item.walk)),
          backgroundColor: 'rgba(63, 131, 138, 0.8)',
          borderColor: '#3f838a',
          borderWidth: 1,
        },
        {
          label: 'Stand',
          data: aggregated.map((item) => toPoint(item.date, item.stand)),
          backgroundColor: 'rgba(204, 228, 244, 0.8)',
          borderColor: '#cce4f4',
          borderWidth: 1,
        },
        {
          label: 'Sit',
          data: aggregated.map((item) => toPoint(item.date, item.sit)),
          backgroundColor: 'rgba(104, 209, 223, 0.8)',
          borderColor: '#68d1df',
          borderWidth: 1,
        },
        {
          label: 'Other',
          data: aggregated.map((item) => toPoint(item.date, item.other)),
          backgroundColor: 'rgba(179, 179, 179, 0.8)',
          borderColor: '#b3b3b3',
          borderWidth: 1,
        },
      ],
    };
  };

  const formatLineChartData = (): ChartData<'line'> => ({
    datasets: [
      {
        label: 'Walk',
        data: data.map((item) => toPoint(item.date, toPct(item.walk, activeTotal(item)))),
        borderColor: '#3f838a',
        backgroundColor: 'rgba(63, 131, 138, 0.1)',
        tension: 0.3,
        pointRadius: 0,
        fill: false,
      },
      {
        label: 'Stand',
        data: data.map((item) => toPoint(item.date, toPct(item.stand, activeTotal(item)))),
        borderColor: '#7aaecc',
        backgroundColor: 'rgba(204, 228, 244, 0.1)',
        tension: 0.3,
        pointRadius: 0,
        fill: false,
      },
      {
        label: 'Sit',
        data: data.map((item) => toPoint(item.date, toPct(item.sit, activeTotal(item)))),
        borderColor: '#68d1df',
        backgroundColor: 'rgba(104, 209, 223, 0.1)',
        tension: 0.3,
        pointRadius: 0,
        fill: false,
      },
      {
        label: 'Other',
        data: data.map((item) => toPoint(item.date, toPct(item.other, activeTotal(item)))),
        borderColor: '#b3b3b3',
        backgroundColor: 'rgba(179, 179, 179, 0.1)',
        tension: 0.3,
        pointRadius: 0,
        fill: false,
      },
    ],
  });

  const timeScaleBase = {
    type: 'time' as const,
    time: {
      unit: timeConfig.unit,
      displayFormats: timeConfig.displayFormats,
      tooltipFormat: timeConfig.tooltipFormat,
    },
    ...axisBounds,
    title: { display: true, text: 'Date / Time' },
    ticks: {
      source: 'auto' as const,
      maxRotation: 45,
      major: { enabled: true },
      font: function(context: any) {
        if (context.tick && context.tick.major) {
          return { weight: 'bold' as const };
        }
      },
    },
  };

  const barScalesOptions = {
    y: {
      beginAtZero: true,
      stacked: true,
      title: { display: true, text: 'Minutes' },
    },
    x: { ...timeScaleBase, stacked: true },
  };

  const lineScalesOptions = {
    y: {
      beginAtZero: true,
      stacked: false,
      min: 0,
      max: 100,
      title: { display: true, text: '% of Active Time' },
      ticks: { callback: (value: any) => `${value}%` },
    },
    x: { ...timeScaleBase, stacked: false },
  };

  const zoomPluginOptions = {
    zoom: {
      wheel: { enabled: false },
      drag: { enabled: true },
      pinch: { enabled: false },
      mode: 'xy' as const,
      onZoomComplete: ({ chart }: any) => {
        if (onZoomChange) onZoomChange(chart);
      },
    },
    pan: {
      enabled: true,
      mode: 'xy' as const,
      onPanComplete: ({ chart }: any) => {
        if (onZoomChange) onZoomChange(chart);
      },
    },
    limits: {
      x: { min: 'original' as const, max: 'original' as const },
      y: { min: 'original' as const, max: 'original' as const },
    },
  };

  const legendOptions = {
    display: true,
    position: 'top' as const,
    labels: { usePointStyle: true, padding: 15 },
  };

  const tooltipTitle = (context: any) => {
    const date = new Date(context[0].parsed.x);
    return date.toLocaleString();
  };

  const barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: barScalesOptions,
    plugins: {
      legend: legendOptions,
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return value !== null ? `${context.dataset.label}: ${value} min` : '';
          },
          title: tooltipTitle,
        },
      },
      zoom: zoomPluginOptions,
    },
  };

  const lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: lineScalesOptions,
    plugins: {
      legend: legendOptions,
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return value !== null ? `${context.dataset.label}: ${value.toFixed(1)}%` : '';
          },
          title: tooltipTitle,
        },
      },
      zoom: zoomPluginOptions,
    },
  };

  if (data.length === 0) {
    return (
      <NoDataContainer sx={{ height }}>
        <Typography variant="body2" color="textSecondary">
          No activity data available
        </Typography>
      </NoDataContainer>
    );
  }

  return (
    <ChartContainer sx={{ height }}>
      {useBarChart
        ? <Bar ref={chartRef} data={formatBarChartData()} options={barChartOptions} />
        : <Line ref={chartRef} data={formatLineChartData()} options={lineChartOptions} />
      }
    </ChartContainer>
  );
};

export default CombinedActivityChart;
